import React from 'react';
import { EquipmentSlot } from '@hexcrawl/shared-utils';
import type { IItemInstanceDTO } from '@hexcrawl/shared-utils';
import { ItemIcon } from './ItemIcon';

// slot → equipped item instance (resolved from inventory by the equipment id map)
export type EquippedItems = Partial<Record<EquipmentSlot, IItemInstanceDTO | undefined>>;

const SLOT_LABELS: Record<EquipmentSlot, string> = {
  [EquipmentSlot.HEAD]: 'Head',
  [EquipmentSlot.BODY]: 'Body',
  [EquipmentSlot.MAIN_HAND]: 'Main Hand',
  [EquipmentSlot.OFF_HAND]: 'Off Hand',
  [EquipmentSlot.ACCESSORY]: 'Accessory',
  [EquipmentSlot.NONE]: 'Relic',
};

const SLOT_ORDER: EquipmentSlot[] = [
  EquipmentSlot.HEAD,
  EquipmentSlot.BODY,
  EquipmentSlot.MAIN_HAND,
  EquipmentSlot.OFF_HAND,
  EquipmentSlot.ACCESSORY,
];

interface EquipmentPanelProps {
  items: EquippedItems;
  onUnequip: (slot: EquipmentSlot) => void;
  hoveredSlot: EquipmentSlot | null;
  onSlotMouseEnter: (slot: EquipmentSlot) => void;
  onSlotMouseLeave: () => void;
}

export const EquipmentPanel: React.FC<EquipmentPanelProps> = ({
  items,
  onUnequip,
  hoveredSlot,
  onSlotMouseEnter,
  onSlotMouseLeave,
}) => (
  <div className="equip-slots-grid">
    {SLOT_ORDER.map(slot => {
      const item = items[slot];
      const hovered = hoveredSlot === slot;
      return (
        <EquipmentSlotCell
          key={slot}
          slot={slot}
          item={item}
          hovered={hovered}
          onMouseEnter={() => onSlotMouseEnter(slot)}
          onMouseLeave={onSlotMouseLeave}
          onClick={() => item && onUnequip(slot)}
        />
      );
    })}
  </div>
);

const EquipmentSlotCell: React.FC<{
  slot: EquipmentSlot;
  item: IItemInstanceDTO | undefined;
  hovered: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
}> = ({ slot, item, hovered, onMouseEnter, onMouseLeave, onClick }) => (
  <div
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    onClick={onClick}
    title={item ? `Unequip ${item.def.name}` : SLOT_LABELS[slot]}
    style={{
      width: 62,
      height: 80,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 3,
      padding: 4,
      background: item
        ? hovered ? 'rgba(90, 160, 255, 0.2)' : 'rgba(20, 40, 60, 0.8)'
        : 'rgba(10, 20, 35, 0.5)',
      border: item
        ? item.def.isQuestItem ? '1px solid #cc9933' : hovered ? '1px solid #ff9966' : '1px solid #5aa0ff'
        : '1px dashed #2a4a6a',
      borderRadius: 6,
      cursor: item ? 'pointer' : 'default',
    }}
  >
    {item ? (
      <>
        <ItemIcon atlas={item.def.icon.atlas} frame={item.def.icon.frame} size={44} />
        <span style={{ fontSize: 8, color: '#8ab0d0', textAlign: 'center', lineHeight: 1.2, wordBreak: 'break-word' }}>
          {item.def.name}
        </span>
      </>
    ) : (
      <>
        <span style={{ fontSize: 18, color: '#2a4a6a' }}>○</span>
        <span style={{ fontSize: 8, color: '#3a5a7a', textAlign: 'center', lineHeight: 1.2 }}>
          {SLOT_LABELS[slot]}
        </span>
      </>
    )}
  </div>
);
